import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase, markSignedOut } from '../supabase/client';
import { isSessionAuthError } from '../supabase/sessionGatedFetch';
import { clearQueryCache, queryClient } from '@/lib/queryClient';
import { logout as logoutService, getProfileRole, isServiceProviderApproved, isAwaitingProviderApproval } from './auth.service';
import { Capacitor } from '@capacitor/core';
import { initializeNativePush, setupPushListeners } from '../capacitor/pushNotifications';
import { registerForPushNotifications, subscribeToForegroundMessages } from '../firebase/messaging';
import { resolveNotificationUrl } from '../notifications/notificationRouter';
import { toast } from 'sonner';

type ProfileRole = 'admin' | 'technician' | 'viewer' | 'employee' | 'member' | null;

/**
 * Last signed-in user id — the owner of the persisted blob a cold start
 * restores. Deliberately NOT removed by clearQueryCache, so it survives a
 * clear. It is shared by ALL tabs, so it only SEEDS each tab's in-memory
 * cache owner (cacheOwnerRef); it is never compared against directly.
 */
const LAST_UID_KEY = 'zentrian_last_uid';

function readLastUid(): string | null {
  try {
    return window.localStorage.getItem(LAST_UID_KEY);
  } catch {
    return null;
  }
}

function writeLastUid(uid: string): void {
  try {
    window.localStorage.setItem(LAST_UID_KEY, uid);
  } catch {
    // Storage unavailable (private mode / quota): isolation then relies on the
    // SIGNED_OUT and logout clears alone.
  }
}

/**
 * Re-run the active queries that failed FOR LACK OF A SESSION (refresh gap
 * after sleep/wake, auth-js refresh cooldown, offline cold start). Only those:
 * auth-js also emits SIGNED_IN on EVERY hidden→visible transition
 * (_recoverAndRefresh), so refetching every errored query here brought back
 * refetch-on-focus — which the app turns off — e.g. re-hitting ThingsBoard
 * during its outage windows on each tab switch, errors a session cannot heal.
 * Deferred out of the auth callback so no Supabase call runs inside it.
 */
function refetchSessionFailedQueries(): void {
  setTimeout(() => {
    void queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => query.state.status === 'error' && isSessionAuthError(query.state.error),
    });
  }, 0);
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  role: ProfileRole;
  isApproved: boolean;
  /** #3556: a 'viewer' whose provider application is waiting for an admin */
  isAwaitingApproval: boolean;
  isPasswordRecovery: boolean;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshRole: () => Promise<void>;
  refreshApprovalStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [role, setRole] = useState<ProfileRole>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [isAwaitingApproval, setIsAwaitingApproval] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const prevUserIdRef = useRef<string | null>(null);
  // Whose data THIS tab's cache holds (undefined = not seeded yet). Per tab on
  // purpose: when another tab signs in as a different user, auth-js lets that
  // tab write LAST_UID_KEY before this tab handles the broadcast SIGNED_IN —
  // comparing against storage read the new uid back and never cleared.
  const cacheOwnerRef = useRef<string | null | undefined>(undefined);

  const syncSession = useCallback(async () => {
    setSessionLoading(true);
    
    // Get session from localStorage (works offline)
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.error('Failed to fetch session', error);
      setSession(null);
      setSessionLoading(false);
      return;
    }

    setSession(data.session);
    setSessionLoading(false);
  }, []);

  const loadRole = useCallback(async () => {
    if (!session?.user) {
      console.log('[AuthProvider] No session user, setting role to null');
      setRole(null);
      return;
    }

    setRoleLoading(true);
    try {
      console.log('[AuthProvider] Loading role for the signed-in user'); // #3220: never log user ids
      
      // Check if offline
      if (!navigator.onLine) {
        console.log('[AuthProvider] Offline - using cached role');
        // Try to get role from cache or user metadata
        const cachedRole = localStorage.getItem('user_role');
        if (cachedRole) {
          setRole(cachedRole as ProfileRole);
          console.log('[AuthProvider] Using cached role'); // #3220: never log role values
        }
        setRoleLoading(false);
        return;
      }
      
      const nextRole = await getProfileRole();
      console.log('[AuthProvider] Role resolved:', nextRole ? 'set' : 'none'); // #3220: never log role values
      setRole(nextRole);
      
      // Cache the role for offline use
      if (nextRole) {
        localStorage.setItem('user_role', nextRole);
      }
    } catch (error) {
      console.error('[AuthProvider] Failed to load profile role', error);
      
      // If error, try to use cached role
      const cachedRole = localStorage.getItem('user_role');
      if (cachedRole) {
        console.log('[AuthProvider] Using cached role after error:', cachedRole);
        setRole(cachedRole as ProfileRole);
      } else {
        setRole(null);
      }
    } finally {
      setRoleLoading(false);
    }
  }, [session]);

  const loadApprovalStatus = useCallback(async () => {
    if (!session?.user) {
      setIsApproved(true);
      setIsAwaitingApproval(false);
      return;
    }

    // Skip approval check if offline - assume approved if we have a session
    if (!navigator.onLine) {
      console.log('[AuthProvider] Offline - assuming approved status');
      const cachedApproval = localStorage.getItem('user_approved');
      setIsApproved(cachedApproval !== 'false');
      // #3556: only a positively proven waiting applicant is cached, as their user id
      setIsAwaitingApproval(localStorage.getItem('user_awaiting_approval') === session.user.id);
      setApprovalLoading(false);
      return;
    }

    setApprovalLoading(true);
    try {
      const [approved, awaiting] = await Promise.all([
        isServiceProviderApproved(),
        isAwaitingProviderApproval().catch(() => false),
      ]);
      setIsApproved(approved);
      setIsAwaitingApproval(awaiting);
      
      // Cache approval status
      localStorage.setItem('user_approved', String(approved));
      if (awaiting) {
        localStorage.setItem('user_awaiting_approval', session.user.id);
      } else {
        localStorage.removeItem('user_awaiting_approval');
      }
    } catch (error) {
      console.error('Failed to load approval status', error);
      
      // Use cached value on error
      const cachedApproval = localStorage.getItem('user_approved');
      setIsApproved(cachedApproval !== 'false');
      setIsAwaitingApproval(false);
    } finally {
      setApprovalLoading(false);
    }
  }, [session]);

  const initPush = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await setupPushListeners();
        await initializeNativePush();
      } else {
        await registerForPushNotifications();
        await subscribeToForegroundMessages((payload) => {
          console.log('[Push] Foreground message:', payload);

          const title = payload.notification?.title || 'Zentrian';
          const body = payload.notification?.body;
          const data = (payload.data || {}) as Record<string, string>;
          const targetUrl = resolveNotificationUrl(data.notification_type, data);

          toast(title, {
            description: body,
            action: {
              label: 'View',
              onClick: () => {
                window.location.href = targetUrl;
              },
            },
            duration: 6000,
          });
        });
      }
    } catch (err) {
      console.error('[Push] Init failed:', err);
    }
  }, []);

  useEffect(() => {
    void syncSession();

    // Seed once, at startup: the restored blob belongs to the last user.
    if (cacheOwnerRef.current === undefined) cacheOwnerRef.current = readLastUid();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      console.log('[AuthProvider] Auth state changed:', event);

      // Cache hygiene — synchronous, no Supabase calls inside this callback.
      if (event === 'SIGNED_OUT') {
        // SDK / cross-tab / revoked-refresh sign-outs never go through
        // handleLogout, so the previous user's cache would otherwise survive.
        clearQueryCache();
        cacheOwnerRef.current = null; // cleared: the cache belongs to no one
      } else if (nextSession) {
        // EVERY event carrying a session is checked (SIGNED_IN, INITIAL_SESSION,
        // TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY): a different user
        // can arrive through a cross-tab broadcast or a visibility recovery.
        const uid = nextSession.user.id;
        const owner = cacheOwnerRef.current;
        if (owner && owner !== uid) {
          // A DIFFERENT user: everything cached (memory + restored blob) is theirs.
          clearQueryCache();
          setIsAwaitingApproval(false); // #3556: never carry it over to another user
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Same user. SIGNED_IN also fires on every hidden→visible transition,
          // not only on a real resume (e.g. mobile offline resume after a null
          // INITIAL_SESSION): keep the offline cache + widget snapshots and heal
          // only what failed for lack of a session. S2 recovery: the session
          // gate cannot outlast auth-js's refresh cooldown.
          refetchSessionFailedQueries();
        }
        cacheOwnerRef.current = uid;
        writeLastUid(uid);
      }

      // Handle password recovery - redirect to reset password page
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[AuthProvider] Password recovery detected');
        // Set flag to prevent auto-redirects
        setIsPasswordRecovery(true);
        // Set the session so reset password page can use it
        setSession(nextSession);
        setSessionLoading(false);
        // Only redirect if not already on reset-password page
        if (window.location.pathname !== '/reset-password') {
          window.location.href = '/reset-password';
        }
        return;
      }

      setIsPasswordRecovery(false);
      setSession(nextSession);
      setSessionLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [syncSession]);

  useEffect(() => {
    const userId = session?.user?.id ?? null;
    if (session?.user) {
      // Only reload role/approval when the user actually changes (sign-in/sign-out).
      // Skip on token refresh (same user, new token) to avoid briefly setting
      // isLoading=true which would unmount ProtectedRoute children (e.g. active chat).
      if (userId !== prevUserIdRef.current) {
        prevUserIdRef.current = userId;
        void loadRole();
        void loadApprovalStatus();
        void initPush();
      }
    } else {
      prevUserIdRef.current = null;
      setRole(null);
      setIsApproved(true);
      setIsAwaitingApproval(false);
    }
  }, [session, loadRole, loadApprovalStatus, initPush]);

  const handleLogout = useCallback(async () => {
    // 1. From here on no PostgREST request can leave with the anon key: the
    //    session gate answers locally (401 AUTH_SESSION_MISSING).
    markSignedOut();
    // 2. In-flight results must not land in the cache after it is cleared.
    await queryClient.cancelQueries();
    try {
      await logoutService();
    } finally {
      // 3. Clear AFTER the session is gone. Clearing first (the old order) made
      //    every mounted observer rebuild and refetch while signOut was in
      //    flight: the profiles 406 / homeowner_members 401 / "view objects"
      //    401 burst, and fail-open results surviving into the next login.
      clearQueryCache();
      setSession(null);
      setRole(null);
      setIsApproved(true);
      setIsAwaitingApproval(false);
      try {
        window.localStorage.removeItem('zentrian_selected_property');
        window.localStorage.removeItem('zentrian_selected_property_name');
      } catch (err) {
        console.warn('Failed to clear selected property cache on logout', err);
      }
    }
  }, []);

  // Graceful revoke handling: if we hold a local session but the server no longer
  // recognizes the user (e.g. an owner removed this member and their account was
  // deleted), sign out with a clear message instead of leaving the app in a 401
  // storm. Only acts on a genuine auth failure (401/403), never a network blip.
  useEffect(() => {
    if (!session?.user) return;
    let cancelled = false;
    const validate = async () => {
      const { error } = await supabase.auth.getUser();
      if (cancelled || !error) return;
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 403) {
        console.warn('[AuthProvider] Session no longer valid (access revoked) — signing out');
        toast.error('Your access has been revoked. Please sign in again.');
        await handleLogout();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void validate();
    };
    window.addEventListener('focus', validate);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', validate);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session, handleLogout]);

  const value = useMemo<AuthContextValue>(() => {
    const user = session?.user ?? null;

    return {
      user,
      session,
      isLoading: sessionLoading || roleLoading || approvalLoading,
      isAuthenticated: Boolean(user),
      role,
      isApproved,
      isAwaitingApproval,
      isPasswordRecovery,
      logout: handleLogout,
      refreshSession: syncSession,
      refreshRole: loadRole,
      refreshApprovalStatus: loadApprovalStatus,
    };
  }, [session, sessionLoading, roleLoading, approvalLoading, role, isApproved, isAwaitingApproval, isPasswordRecovery, handleLogout, syncSession, loadRole, loadApprovalStatus]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: Exclude<ProfileRole, null>;
}

interface ProtectedRouteProps extends RoleProtectedRouteProps {
  forbidRoles?: Exclude<ProfileRole, null>[];
  redirectTo?: string;
  show404?: boolean; // If true, show 404 page instead of redirecting
}

export const ProtectedRoute = ({
  children,
  requiredRole,
  forbidRoles,
  redirectTo,
  show404 = false,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, role, isApproved, isAwaitingApproval, refreshRole } = useAuth();
  const location = useLocation();
  const [retryCount, setRetryCount] = React.useState(0);

  // If role is null but we're authenticated and not loading, try refreshing the role
  // Give it a few retries in case of race conditions
  React.useEffect(() => {
    if (isAuthenticated && !isLoading && role === null && requiredRole && retryCount < 3) {
      console.log('[ProtectedRoute] Role is null, attempting to refresh... (retry', retryCount + 1, ')');
      const timer = setTimeout(() => {
        refreshRole();
        setRetryCount(prev => prev + 1);
      }, 100 * (retryCount + 1)); // Exponential backoff
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isLoading, role, requiredRole, refreshRole, retryCount]);

  // Wait a bit longer if role is null but we're authenticated (might still be loading)
  if (isLoading || (isAuthenticated && role === null && retryCount < 3)) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Block unapproved service providers from all protected routes
  // Redirect to approval pending page instead of login
  // #3556: viewers only on positive proof that their application is waiting
  if ((!isApproved && role === 'technician') || (isAwaitingApproval && role === 'viewer')) {
    return <Navigate to="/service-provider/approval-pending" replace />;
  }

  if (requiredRole && role !== requiredRole) {
    console.log('[ProtectedRoute] Role mismatch:', { requiredRole, currentRole: role, path: location.pathname, isAuthenticated, isLoading, retryCount });
    if (show404) {
      return <Navigate to="/404" replace />;
    }
    return <Navigate to={redirectTo ?? '/dashboard'} replace />;
  }

  if (forbidRoles && role && forbidRoles.includes(role)) {
    if (show404) {
      return <Navigate to="/404" replace />;
    }
    // Special handling: redirect technicians to their dashboard
    if (role === 'technician' && location.pathname === '/dashboard') {
      return <Navigate to="/service-provider/dashboard" replace />;
    }
    return <Navigate to={redirectTo ?? '/dashboard'} replace />;
  }

  return <>{children}</>;
};
